<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-brand-configuration-volume" v-loading="loading">
    <div class="title-btns-section"> 
      <div v-if="isC1Interactions" class="title">Volume alerts</div>
      <div class="update-btns">
        <el-button :class="(hasError||!hasNoSavedChanges?'primary-btn update-btn disabled':'primary-btn update-btn')"  :disabled="hasError||!hasNoSavedChanges" @click="updateSettings">Update</el-button>
      </div>
    </div>
    <div class="btn-section">
    </div>
    <div class="title-description-content">
      <div v-if="isC1Interactions" class="title-description"><span>Set the threshold for when a topic appears as New.</span></div>
      <div v-else class="title-description"><span>&nbsp;</span></div>
      <div v-if="!loading&&hasVolume" class="volume-status"><span class="has-alerts"></span>Alerts setup</div>
      <div v-else-if="!loading&&!hasVolume" class="volume-status"><span class="has-no-alerts"></span>No alerts setup</div>
    </div>
    
    <div class="content-section last-section">
        <div class="volume-section">
          <div>
            <div class="section-title">Minimum average daily volume</div>
            <div class="item-title">How many contacts per day should contain this topic for the topic to appear on the dashboard?</div>
            <vue-numeric v-bind:minus="false" separator="," v-bind:precision="0" v-bind:min="0" :class="{'no-value':configurationData.topicLowVolumeThreshold===''}" v-model="configurationData.topicLowVolumeThreshold" placeholder="Number of contacts"></vue-numeric> 
          </div>

          <div class="item-title" v-if="isSpecialUser"> 
            <div class="section-title">Topic Confidence Threshold (Show this input for QA test only, remove this after QA finished testing)</div>
            <vue-numeric v-bind:minus="false" v-bind:precision="2" v-bind:max="0.99" v-bind:min="0.01" :class="{'no-value':configurationData.topicConfidenceThreshold===''}" v-model="configurationData.topicConfidenceThreshold" placeholder="Enter a number"></vue-numeric> 
          </div> 
        </div>
      </div>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import VueNumeric from 'vue-numeric'

  export default {
    name: 'wy-brand-configuration-volume',
    components: {
      VueNumeric
    },
    props:  ['controller'],
    
    data() {
      return {
        module: this,
        loading: false,
        companyConfigSettings: {},
        configurationData: {
          topicLowVolumeThreshold: 10,
          topicConfidenceThreshold: 0.2,
        },
        configurationDataClone: {},
        messageMap: {
          topicLowVolumeThreshold: {
            description: 'Number of contacts',
            placeholder: 'Number of ...'
          },
          topicConfidenceThreshold: {
            description: 'Enter the contacts.',
            placeholder: 'Number of confidence threshold'
          },
        },
        billingDate: '',
        tableData: [],
        tableDataClone: [],
        disableClearAllBtn: false
      };
    },


    computed: {
      ...mapGetters(['dataModel', 'isRoleAllow', 'isC1Interactions', 'isSpecialUser']),

      hasVolume() {
        return this.companyConfigSettings && this.companyConfigSettings.settings && this.companyConfigSettings.settings.topicLowVolumeThreshold
      },

      hasError() {
        let hasError = false;
        Object.keys(this.configurationData).forEach(key => {
          if(this.configurationData[key] === '' || this.configurationData[key] == null) {
            hasError = true
          }
        })
        return hasError;
      },

      hasNoSavedChanges() {
        return (JSON.stringify(this.configurationData) !== JSON.stringify(this.configurationDataClone))
      },
    },

    methods: {
      ...mapActions(['updateCompanyConfigSettings', 'updateCompanyConfigSettings', 'fetchCompanyConfigSettings']),


      handleDiscardUnsavedChanges() {
        this.controller.hasError = false;
        this.configurationData = JSON.parse(JSON.stringify(this.configurationDataClone));
      },

      async updateSettings() {
        let updateData = {}
        // updateData.volume = null
        updateData.key = 'company';
        updateData.settings = this.configurationData;
        // updateData.settings = {}
        this.loading = true;
        let success = await this.updateCompanyConfigSettings(updateData);
        // let success = true;
        if(success) {
          this.loading = false;
          // this.controller.hasNoSavedChanges = false;
          this.controller.isDashBoardModified = true;
          this.configurationDataClone = JSON.parse(JSON.stringify(this.configurationData));
          this.$message({
            showClose: true,
            message: this.$t("configurations.volumeUpdateSuccess"),
            customClass: 'configuration-success-message',
            iconClass: 'el-icon-circle-check',
            duration: 5000,
            type: 'success'
          });
          this.$router.push({ name: "reload", params: {reload: this.$route.path}});
        }
        else {
          this.loading = false;
          this.$message({
            showClose: true,
            message: this.$t("configurations.volumeUpdateFailed"),
            customClass: 'configuration-update-failed-message',
            duration: 5000,
            type: 'error'
          });
        }
      },

      handleSaveUnchanges(newTab, handleAppModalClose) {
        this.controller.$refs.updateDialog.title = `Review changes`;
        this.controller.$refs.updateDialog.content="There are unsaved changes. Do you want to discard or save them before leaving?" 
        this.controller.$refs.updateDialog.open();
        if(this.hasError) {
          this.controller.$refs.updateDialog.disableSaveBtn = true;
        }
        else {
          this.controller.$refs.updateDialog.disableSaveBtn = false;
        }
        this.controller.$refs.updateDialog.confirmButtonCallback = async () => {
          await this.updateSettings()
          if(handleAppModalClose) {
            handleAppModalClose();
          }
          else {
            this.controller.handleTabClick({name: newTab})
            this.controller.activeTab = newTab
          }
        }
        this.controller.$refs.updateDialog.discardButtonCallback = async () => {
          this.handleDiscardUnsavedChanges()
          if(handleAppModalClose) {
            handleAppModalClose();
          }
          else {
            this.controller.handleTabClick({name: newTab})
            this.controller.activeTab = newTab
          }
        }
      },

      async prepareData() {
        this.loading = true;
        this.companyConfigSettings = await this.fetchCompanyConfigSettings({key: 'company'})

        if(!this.companyConfigSettings.settings) {
          this.companyConfigSettings.settings = {}
        }
        if(this.companyConfigSettings.settings.topicLowVolumeThreshold === undefined) {
          this.companyConfigSettings.settings.topicLowVolumeThreshold = 10;
        }
        if(this.companyConfigSettings.settings.topicConfidenceThreshold === undefined) {
          this.companyConfigSettings.settings.topicConfidenceThreshold = 0.2;
        }

        this.configurationData = JSON.parse(JSON.stringify(this.companyConfigSettings.settings));
        this.configurationDataClone = JSON.parse(JSON.stringify(this.configurationData));
        this.loading = false;
        setTimeout(() => {
          if(document.querySelectorAll('.volume-section') && document.querySelectorAll('.volume-section').length > 0) {
            document.querySelectorAll('.volume-section input').forEach(el => {
              el.addEventListener('keypress',(e)=>{
                var keyCode = e.keyCode || e.which;
                if ((keyCode < 48 || keyCode > 57) || keyCode == 46) {
                  e.preventDefault();
                  return false;
                }
              })
            })
          }
        }, 100)
      }
    },

    async created() {
      this.prepareData();
    },
  };
</script>

<style lang="scss">
  .wy-brand-configuration-volume {
    min-width: 530px;
    height: inherit;

    .volume-status {
      position: relative;
      top: -3px;
      right: 0px;
      margin-top: 0px;
      display: block;
      text-align: right;
      font-size: 14px;
      font-weight: 400;
      width: 128px;
    }

    .has-alerts {
      background-image: url('../../assets/icons/icon_has-alerts.svg') !important;
      height: 16px;
      width: 16px;
      background-size: 15px;
      background-repeat: no-repeat;
      background-position-y: 0px;
      background-position-x: 0px;
      display: inline-block;
      position: relative;
      top: 4px;
      left: -6px;
    }
    .has-no-alerts {
      background-image: url('../../assets/icons/icon_no-alerts.svg') !important;
      height: 16px;
      width: 16px;
      background-size: 15px;
      background-repeat: no-repeat;
      background-position-y: 0px;
      background-position-x: 0px;
      display: inline-block;
      position: relative;
      top: 4px;
      left: -6px;
    }
    div.content-section.last-section {
      border-bottom: 0px solid #CCC;
    }
    .content-section {
      height: fit-content;
      border-bottom: 1px #CCC solid;
      margin: 0px 20px 0px 20px;
      padding-top: 25px;
      padding-bottom: 25px;

      .billing-cycle {
        input.el-input__inner {
          border-width: 1px !important;
        }
        .el-input--prefix .el-input__inner {
          padding-left: 10px;
          border-radius: 5px;
          border: 1px solid #106fbc;
          background-color: #e8f1ff;
        }
        .el-input--prefix.no-value .el-input__inner {
          background-color: #fff4f4;
          border: 1px solid #ebafba;
        }
        .el-input__prefix {
          right: 5px !important;
          left: unset;
        }

        .el-input__suffix {
          right: 25px !important;
        }

        .item-title, .demonstration{
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          color: #2A2E32;
          padding-bottom: 15px;
          padding-top: 15px;
          padding-right: 10px;
        }
      }
      
      .section-title {
        font-size: 16px;
        font-style: normal;
        color: #2A2E32;
        font-weight: 500;
        padding-bottom: 5px;
      }
      .volume-section {
        .item-title {
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          color: #2A2E32;
          padding-bottom: 15px;
          padding-top: 15px;
          .item-subtitle {
            font-size: 14px;
            font-style: normal;
            font-weight: 500;
            padding-top: 5px;
            padding-bottom: 15px;
          }
        }
        input.no-value {
          background-color: #fff4f4;
          border: 1px solid #ebafba;
        }
        input {
          // padding: 5px 20px 5px 20px;
          height: 30px;
          width: 216px;
          border: 1px solid #106fbc;
          border-radius: 5px;
          text-align: left;
          background-color: #e8f1ff;
          outline: none;
          padding-left: 10px;
          padding-right: 20px;
        }
        input::placeholder {
          text-align: left;
        }
        input:-ms-input-placeholder {
          text-align: left;
        }
        .percentage-sign {
          // padding-left: 20px;
          position: relative;
          left: -20px;
        }
      }
      .volume-section.topicLowVolumeThreshold {
        input {
          width: 216px;
        }
      }
      .volume-section.volumeAlertPercentage {
        input {
          width: 216px;
        }
      }
    }
    .wy-genaric-table .wy-table-wrapper {
      border-radius: 0px !important;
      height: calc(100% - 75px);
    }
    .wy-genaric-table .wy-table-fixed tbody tr td:first-child {
      background-color: #FFFF;
    }
    .wy-genaric-table .wy-table-main thead tr {
      width: 100%;
      width: calc(100vw - 372px);
    }
    .wy-table-main thead {
      background-color: #FFF;
      border: 1px solid #CAD2DA;
      width: calc(100vw - 372px);
    }
    .wy-table-main thead th {
      background-color: #FFF;
      text-align: left !important;
    }
    .wy-genaric-table .wy-table-main tbody {
      
      // height: calc( 100vh - 488px);
      height: calc( 100vh - 355px);
      overflow: hidden;
      width: calc(100vw - 372px);
      border-right: 1px solid #CAD2DA;
      border-left: 1px solid #CAD2DA;
      border-bottom: 1px solid #CAD2DA;
    }
    .wy-genaric-table tbody tr td {
      border-bottom: 0px solid #eee;
      div {
        // margin-left: 16px;
        justify-content: left;
      }
    }
    .title-btns-section {
      display: inline-flex;
      width: 100%;
      margin-bottom: 10px;
      .title {
        line-height: 2;
        font-size: 16px;
        font-weight: 700;
      }
      .update-btns {
        display: inline-flex;
        margin-left: auto;
      }
      .update-cancel-btn {
        color: #202223 !important;
        font-family: Helvetica Neue, Helvetica, Arial, sans-serif;
        font-size: 15px;
        font-weight: 400;
        padding: 8px 16px 8px 16px;
        display: flex;
        border-radius: 5px;
        height: fit-content;
        width: fit-content;
        border: 1px solid #CAD2DA;
        cursor: pointer;
        background: white;
        margin-right: 15px;
        align-items: center;
        justify-content: center;
        &:hover{
          background: #F4F6F8;
        }
      }
      .primary-btn.update-btn {
        width: 98px;
        padding-bottom: 6px !important;
      }
      .update-save-btn {
        width: fit-content;
        height: fit-content;
        padding: 8px 16px 8px 16px;
        font-size: 15px;
        cursor: pointer;
        background: var(--site-button-primary-btn-background-primary-color);
        color: var(--site-button-primary-btn-text-primary-color) !important;
        border: 1px solid var(--site-button-primary-btn-hover-border-primary-color);
        font-family: Helvetica Neue, Helvetica, Arial, sans-serif;
        border-radius: 5px;
        // margin-right: 15px;
        align-items: center;
        justify-content: center;
      }
      .update-save-btn:hover {
        border: 1px var(--site-button-primary-btn-hover-border-primary-color) solid !important;
        background-color: var(--site-button-primary-btn-hover-background-primary-color) !important;
        border-radius: 5px;
        color: var(--site-button-primary-btn-text-primary-color) !important;
      }
      .update-save-btn:active {
        background-color: var(--site-button-primary-btn-pressed-background-primary-color) !important;
      }
      .update-save-btn:focus {
        border: 1px var(--site-button-primary-btn-hover-border-primary-color) solid !important;
        border-radius: 5px;
        color: var(--site-button-primary-btn-text-primary-color) !important;
      }
      .update-save-btn.disabled {
        background: var(--site-button-primary-btn-disabled-background-primary-color);
        cursor: not-allowed;
      }
      .update-save-btn.disabled:hover {
        border: 1px var(--site-button-primary-btn-hover-border-primary-color) solid !important;
        background: var(--site-button-primary-btn-disabled-background-primary-color) !important;
        border-radius: 5px;
        color: var(--site-button-primary-btn-text-primary-color) !important;
      }
      .update-save-btn.disabled:active {
        background: var(--site-button-primary-btn-disabled-background-primary-color) !important;
      }
      .update-save-btn.disabled:focus {
        border: 1px var(--site-button-primary-btn-hover-border-primary-color) solid !important;
        border-radius: 5px;
        color: var(--site-button-primary-btn-text-primary-color) !important;
      }
      .intent-action-dropdown-save-btn:disabled{
        background: #C0C4CC;
        cursor: not-allowed;
      }
    }
    .btn-section {
      display: flex;
      float: right;
      // width: 425px;
      .clear-all-btn {
        font-size: 12px;
        width: 105px;
        text-align: right;
        line-height: 1;
        color: #106FBC;
        cursor: pointer;
      }
      .clear-all-btn.disabled{
        pointer-events: none;
        color: #CCC;
      }
      .reset-all-btn {
        font-size: 12px;
        width: 142px;
        line-height: 1;
        color: #106FBC;
        cursor: pointer;
      }
    }
    .title-description-content {
      font-size: 14px;
      font-weight: 400;
      padding-top: 8px;
      padding-bottom: 30px;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      .title-description {
        width: calc(100% - 100px);
      }
    }
    // .el-loading-mask {
    //   height: calc(100vh - 400px);
    // }
  }
  .wy-brand-configuration-volume > .content-section {
    border-top: 1px #CCC solid;
    .item-title {
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      color: #2A2E32;
      padding-bottom: 0px;
      padding-top: 15px;
      padding-right: 10px;
      max-width: 800px;
    }
    .billing-cycle.block {
      max-width: 800px;
    }
  }

  .wy-brand-configuration-volume > .content-section ~ .content-section {
    border-top: 0px;
  }

  .configuration-success-message {
    background-color: #D9EEDF;
    border-color: #86D2AE;
    padding: 10px 10px 10px 10px;
    max-width: 300px;
    .el-icon-circle-check {
      color: #186731;
      margin-right: 10px;
      margin-left: 5px;
    }
     .el-message__content {
      color: var(--site-font-secondary-color);
      padding-right: 30px;
      line-height: 1.2;
      font-size: 14px;
      font-weight: 400;
      padding-right: 25px;
    }
    .el-message__closeBtn.el-icon-close {
      color: #4A4A4F;
      border-left: 1px solid #4A4A4F;
      padding-left: 5px;
    }
  }
  .configuration-update-failed-message {
    padding: 10px 10px 10px 10px;
    max-width: 300px;
    .el-message__content {
      color: var(--site-font-secondary-color);
      padding-right: 30px;
      line-height: 1.2;
      font-size: 14px;
      font-weight: 500 !important;
      padding-right: 25px;
    }
    .el-message__closeBtn.el-icon-close {
      color: #4A4A4F;
      border-left: 1px solid #4A4A4F;
      padding-left: 5px;
    }
  }
</style>